import { useEffect, useRef } from "react"

// ──────────────────────────────────────────────────────────────────────────
// STRINGERE E ALLARGARE LE DITA
//
// Come nei calendari: stringendo due dita si vede di più (dal giorno alla
// settimana), allargandole si vede da vicino (dalla settimana al giorno).
// Lo swipe di lato non si può usare: sulle lezioni apre la modifica.
//
// Il passaggio segue le dita: a metà gesto la pagina è a metà strada, e
// tornando indietro con le dita torna indietro anche lei. Alzandole si va
// fino in fondo, o si torna da dove si era partiti se il gesto è stato
// timido. Lo zoom del browser è già spento (`touch-action` in styles.css),
// quindi il pizzico è tutto nostro.
// ──────────────────────────────────────────────────────────────────────────

// Quanto bisogna stringere per arrivare da un capo all'altro: dimezzare la
// distanza tra le dita (o raddoppiarla, all'indietro) vale un passaggio e un
// po'. Si misura in proporzione, così stringere e allargare pesano uguale.
const PINCH_SPEED = 1.25

// Dove porta un pizzico, partito da `from` (0 il giorno, 1 la settimana),
// con le dita a `scale` volte la distanza di partenza.
export const pinchProgress = (from, scale) => {
	if (!scale || scale <= 0) return from
	return Math.min(1, Math.max(0, from - PINCH_SPEED * Math.log2(scale)))
}

// Alzate le dita, dove si va a finire. Basta un quarto di strada per
// cambiare vista: il gesto è già una scelta, non serve insistere.
const COMMIT = 0.25
export const pinchTarget = (from, progress) =>
	from < 0.5 ? (progress >= COMMIT ? 1 : 0) : progress <= 1 - COMMIT ? 0 : 1

const distanceOf = touches =>
	Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY)

const midpointOf = touches => ({
	x: (touches[0].clientX + touches[1].clientX) / 2,
	y: (touches[0].clientY + touches[1].clientY) / 2,
})

// onStart riceve il punto in mezzo alle due dita, da dove il gesto parte:
// allargando su un giorno della settimana si apre quello. onChange riceve
// a ogni movimento quante volte le dita sono più vicine o lontane di prima,
// onEnd lo stesso quando si alzano.
const usePinchToSwitchView = ({ enabled, onStart, onChange, onEnd }) => {
	const handlers = useRef({ onStart, onChange, onEnd })
	handlers.current = { onStart, onChange, onEnd }

	useEffect(() => {
		if (!enabled) return

		let gesture = null
		const options = { passive: true }

		// Le dita restano legate a quello che hanno toccato all'inizio: se nel
		// gesto quel pezzo di pagina viene rifatto — allargando su un giorno
		// della settimana succede — i movimenti arrivano a lui e non salgono
		// più fino al documento. Si ascoltano anche lì.
		let touched = []
		const listenOn = targets => {
			touched = targets.filter(target => target && target !== document)
			touched.forEach(target => {
				target.addEventListener("touchmove", move, options)
				target.addEventListener("touchend", end, options)
				target.addEventListener("touchcancel", end, options)
			})
		}
		const stopListening = () => {
			touched.forEach(target => {
				target.removeEventListener("touchmove", move, options)
				target.removeEventListener("touchend", end, options)
				target.removeEventListener("touchcancel", end, options)
			})
			touched = []
		}

		const finish = () => {
			stopListening()
			if (!gesture) return
			const { scale } = gesture
			gesture = null
			handlers.current.onEnd?.(scale)
		}

		const start = event => {
			// con tre dita non è un pizzico
			if (event.touches.length !== 2) {
				finish()
				return
			}
			gesture = { distance: distanceOf(event.touches), scale: 1 }
			listenOn([...new Set(Array.from(event.touches, touch => touch.target))])
			handlers.current.onStart?.(midpointOf(event.touches))
		}

		// lo stesso movimento può arrivare due volte, dal punto toccato e dal
		// documento: conta solo la distanza tra le dita, quindi non cambia niente
		function move(event) {
			if (!gesture || event.touches.length !== 2 || !gesture.distance) return
			gesture.scale = distanceOf(event.touches) / gesture.distance
			handlers.current.onChange?.(gesture.scale)
		}

		function end(event) {
			if (event.touches.length < 2) finish()
		}

		document.addEventListener("touchstart", start, options)
		document.addEventListener("touchmove", move, options)
		document.addEventListener("touchend", end, options)
		document.addEventListener("touchcancel", end, options)
		return () => {
			document.removeEventListener("touchstart", start, options)
			document.removeEventListener("touchmove", move, options)
			document.removeEventListener("touchend", end, options)
			document.removeEventListener("touchcancel", end, options)
			stopListening()
		}
	}, [enabled])
}

export default usePinchToSwitchView
